import { useEffect, useMemo, useState } from 'react'
import { Link, useLocation, useParams } from 'react-router-dom'
import { api, ApiError } from '../api'
import type { HistoricalFigure } from '../api/apiClient'
import SourceDepthBadge from '../components/SourceDepthBadge'
import { FIGURES } from '../data/figures'
import { PLACES } from '../data/places'
import { usePageMetadata } from '../hooks/usePageMetadata'
import { getApiBase, resolveMediaUrl } from '../utils/media'
import { getRelatedPlaceSlugs } from '../utils/figures'
import DOMPurify from 'dompurify'
import { marked } from 'marked'

const fallbackFigureImage = '/images/figure-fallback.svg'
const FIGURE_LIST_LIMIT = 400

type FigureLike = HistoricalFigure & {
  primaryEra?: string
  era_primary?: string
  summary?: string
  teaser?: string
  imageUrl?: string
  pitNarrative?: string | null
  pitNarrativeUpdatedAt?: string | null
  pitNarrativeFormat?: 'markdown' | 'plain' | null
}

type MiniTimelineItem = {
  label?: string
  text?: string
}

const PLACE_NAME_MAP = new Map((PLACES ?? []).map((place) => [place.slug, place.name]))

const normalizeSlug = (value: string): string => value.trim().toLowerCase()

const formatPlaceLabel = (slug: string): string => {
  const resolved = PLACE_NAME_MAP.get(slug)
  if (resolved) {
    return resolved
  }
  return slug
    .split('-')
    .map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1))
    .join(' ')
}

const buildEra = (figure: FigureLike): string =>
  figure.era ?? figure.primaryEra ?? figure.era_primary ?? ''

const buildTeaser = (figure: FigureLike): string =>
  figure.short_summary ?? figure.teaser ?? figure.summary ?? ''

const splitParagraphs = (text: string): string[] =>
  text
    .split(/\n{2,}/)
    .map((chunk) => chunk.trim())
    .filter((chunk) => chunk.length > 0)

const stripMarkdown = (text: string): string =>
  text
    .replace(/```[\s\S]*?```/g, '')
    .replace(/`([^`]+)`/g, '$1')
    .replace(/\*\*([^*]+)\*\*/g, '$1')
    .replace(/\*([^*]+)\*/g, '$1')
    .replace(/_([^_]+)_/g, '$1')
    .replace(/^#+\s+/gm, '')
    .replace(/\[([^\]]+)\]\([^\)]+\)/g, '$1')
    .replace(/<[^>]+>/g, '')

const renderMarkdownSafe = (markdownText: string): string => {
  const rawHtml =
    typeof marked.parse === 'function'
      ? marked.parse(markdownText, { async: false })
      : marked(markdownText)
  return DOMPurify.sanitize(rawHtml as string, { USE_PROFILES: { html: true } })
}

const parseJsonField = (value: unknown, fallback: unknown[] = []) => {
  if (!value) {
    return fallback
  }

  if (Array.isArray(value)) {
    return value
  }

  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value)
      return Array.isArray(parsed) ? parsed : fallback
    } catch {
      return fallback
    }
  }

  return fallback
}

const toStringArray = (value: unknown): string[] => {
  if (!value) {
    return []
  }

  if (Array.isArray(value)) {
    return value
      .map((entry) => (typeof entry === 'string' ? entry : String(entry ?? '')).trim())
      .filter((entry) => entry.length > 0)
  }

  if (typeof value === 'string') {
    const trimmed = value.trim()
    if (!trimmed) {
      return []
    }
    if (trimmed.startsWith('[')) {
      const parsed = parseJsonField(trimmed, [])
      if (Array.isArray(parsed)) {
        return parsed
          .map((entry) => (typeof entry === 'string' ? entry : String(entry ?? '')).trim())
          .filter((entry) => entry.length > 0)
      }
    }
    return trimmed.split(',').map((segment) => segment.trim()).filter((segment) => segment.length > 0)
  }

  return []
}

const formatLifespan = (figure: FigureLike): string | null => {
  const hasBirth = typeof figure.birth_year === 'number'
  const hasDeath = typeof figure.death_year === 'number'
  if (hasBirth || hasDeath) {
    const birth = hasBirth ? figure.birth_year : '?'
    const death = hasDeath ? figure.death_year : '?'
    return `${birth ?? '?'} – ${death ?? '?'}`
  }

  const hasFloruitStart = typeof figure.floruit_start_year === 'number'
  const hasFloruitEnd = typeof figure.floruit_end_year === 'number'
  if (hasFloruitStart || hasFloruitEnd) {
    const start = hasFloruitStart ? figure.floruit_start_year : '?'
    const end = hasFloruitEnd ? figure.floruit_end_year : '?'
    return `fl. ${start ?? '?'} – ${end ?? '?'}`
  }

  return null
}

const Person = () => {
  const location = useLocation()
  const { slug } = useParams<{ slug: string }>()
  const [figure, setFigure] = useState<FigureLike | null>(null)
  const [figureDirectory, setFigureDirectory] = useState<Record<string, { name?: string }>>({})
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [notFound, setNotFound] = useState(false)
  const [showFullNarrative, setShowFullNarrative] = useState(false)
  const searchParams = new URLSearchParams(location.search)
  const startChatIntent = searchParams.get('start_chat') === '1'

  const metadataTitle = figure
    ? `${figure.name} | Places in Time`
    : notFound
      ? 'Figure Not Found | Places in Time'
      : 'Historical Figure | Places in Time'
  const metadataDescription = figure
    ? `Discover ${figure.name}, their historical role, connected places, and the wider context behind their life and legacy.`
    : notFound
      ? 'This historical figure could not be found in Places in Time.'
      : 'Discover historical figures, connected places, and wider context across Britain.'

  usePageMetadata({
    title: metadataTitle,
    description: metadataDescription,
    path: slug ? `/people/${slug}` : '/people',
    type: figure ? 'article' : 'website',
  })

  // IMPORTANT: derive narrative + format safely BEFORE any returns so hooks below stay consistent.
  const pitNarrativeRaw = figure?.pit_narrative ?? figure?.pitNarrative ?? ''
  const pitNarrative = typeof pitNarrativeRaw === 'string' ? pitNarrativeRaw.trim() : ''
  const pitNarrativeFormat = (figure?.pit_narrative_format ??
    figure?.pitNarrativeFormat ??
    'plain') as 'markdown' | 'plain' | string

  const markdownSections = useMemo(() => {
    if (pitNarrativeFormat !== 'markdown') return null
    if (!pitNarrative) return { intro: '', sections: [] as { title: string; body: string }[] }

    const lines = pitNarrative.split('\n')
    const introLines: string[] = []
    const sections: { title: string; body: string }[] = []
    let current: { title: string; bodyLines: string[] } | null = null

    lines.forEach((line) => {
      const headingMatch = line.match(/^##\s+(.+)/)
      if (headingMatch) {
        if (current) {
          sections.push({ title: current.title.trim(), body: current.bodyLines.join('\n').trim() })
        }
        current = { title: headingMatch[1], bodyLines: [] }
      } else if (current) {
        current.bodyLines.push(line)
      } else {
        introLines.push(line)
      }
    })

    if (current) {
      sections.push({ title: current.title.trim(), body: current.bodyLines.join('\n').trim() })
    }

    return {
      intro: introLines.join('\n').trim(),
      sections: sections.filter((section) => section.title && section.body),
    }
  }, [pitNarrative, pitNarrativeFormat])

  const hasAccordionSections = Boolean(markdownSections && markdownSections.sections.length >= 2)

  useEffect(() => {
    let cancelled = false

    if (!slug) {
      setError('Missing person identifier')
      setLoading(false)
      return () => {
        cancelled = true
      }
    }

    const fetchFigure = async () => {
      const targetSlug = normalizeSlug(slug)

      try {
        const figures = await api.listFigures({ limit: FIGURE_LIST_LIMIT })
        if (!cancelled && Array.isArray(figures) && figures.length > 0) {
          const directory = figures.reduce<Record<string, { name?: string }>>((acc, entry) => {
            const normalizedSlug = normalizeSlug(entry.slug)
            if (!acc[normalizedSlug]) {
              acc[normalizedSlug] = { name: entry.name }
            }
            return acc
          }, {})
          setFigureDirectory(directory)
        }
        const matched = figures.find((entry) => normalizeSlug(entry.slug) === targetSlug) as
          | FigureLike
          | undefined

        if (matched && !cancelled) {
          setFigure(matched)
          setNotFound(false)
          setError(null)
          setLoading(false)
          return
        }
      } catch (listError) {
        if (import.meta.env.DEV) {
          // eslint-disable-next-line no-console
          console.warn('[PERSON_PAGE] Failed to load enriched figure list; falling back to single fetch', listError)
        }
      }

      try {
        const data = await api.getFigure(slug)
        if (!cancelled) {
          setFigure(data)
          setNotFound(false)
          setError(null)
          setFigureDirectory((prev) => ({
            ...prev,
            [normalizeSlug(data.slug)]: { name: data.name },
          }))
        }
      } catch (err) {
        if (cancelled) {
          return
        }

        const fallback = FIGURES.find((entry) => entry.slug === slug) as FigureLike | undefined

        if (err instanceof ApiError && err.status === 404 && !fallback) {
          setNotFound(true)
          setFigure(null)
          setError('Person not found')
        } else if (fallback) {
          setFigure(fallback)
          setNotFound(false)
          setError('Showing preview data while the live profile loads.')
        } else {
          setNotFound(false)
          setError(err instanceof Error ? err.message : 'Failed to load person')
        }
      } finally {
        if (!cancelled) {
          setLoading(false)
        }
      }
    }

    fetchFigure()

    return () => {
      cancelled = true
    }
  }, [slug])

  useEffect(() => {
    setShowFullNarrative(false)
  }, [slug])

  if (loading) {
    return (
      <section className="detail-page">
        <p>Loading person…</p>
      </section>
    )
  }

  if (!figure) {
    return (
      <section className="detail-page">
        <h1>{notFound ? 'Person not found' : 'Unable to load person'}</h1>
        <p>{error ?? 'The chronicles you seek may sit under a different name.'}</p>
        <Link className="button" to="/people">
          Back to all people
        </Link>
      </section>
    )
  }

  const era = buildEra(figure)
  const teaser = buildTeaser(figure)
  const portrait = figure.image_url ?? figure.imageUrl
  const portraitSrc = resolveMediaUrl(getApiBase(), portrait) ?? fallbackFigureImage
  const lifespan = formatLifespan(figure)
  const eraLabel = figure.era_label ?? era
  const heroMetaLine = [lifespan, eraLabel].filter(Boolean).join(' • ')
  const knownFor = figure.known_for ?? teaser ?? null
  const summaryCopy = figure.summary ?? figure.short_summary ?? figure.summary_gen ?? teaser ?? ''
  const rolesList = toStringArray(figure.roles)
  const rolesLine = rolesList.length > 0 ? rolesList.join(', ') : undefined
  const relatedPlaceSlugs = Array.from(new Set(getRelatedPlaceSlugs(figure)))
  const primaryPlaceSlug = relatedPlaceSlugs[0] ?? null
  const associatedPlaceMetadata = toStringArray(figure.associated_places)
  const fallbackAssociatedPlaces =
    relatedPlaceSlugs.length > 0
      ? relatedPlaceSlugs.map((slug) => ({ slug, label: formatPlaceLabel(slug) }))
      : []
  const associatedPlaces =
    associatedPlaceMetadata.length > 0
      ? associatedPlaceMetadata.map((entry) => {
          const slugMatch = PLACE_NAME_MAP.has(entry) ? entry : undefined
          return {
            slug: slugMatch,
            label: slugMatch ? PLACE_NAME_MAP.get(entry) ?? entry : entry,
          }
        })
      : fallbackAssociatedPlaces
  const pronunciation = figure.pronunciation?.trim() ?? null
  const miniTimelineItems = (parseJsonField(figure.mini_timeline, []) as MiniTimelineItem[]).filter(
    (item) => item && (item.label || item.text)
  )

  const pitNarrativeTeaserLimit = 200
  const pitNarrativeTextForTeaser = stripMarkdown(pitNarrative)
  const pitNarrativeTeaserText =
    pitNarrativeTextForTeaser.length > pitNarrativeTeaserLimit
      ? pitNarrativeTextForTeaser.slice(0, pitNarrativeTeaserLimit).replace(/\s+\S*$/, '') ||
        pitNarrativeTextForTeaser.slice(0, pitNarrativeTeaserLimit)
      : pitNarrativeTextForTeaser
  const pitNarrativeParagraphs = splitParagraphs(pitNarrative)
  const pitNarrativeTeaserParagraphs = splitParagraphs(pitNarrativeTeaserText)
  const shouldTruncateNarrative = pitNarrative.length > pitNarrativeTeaserText.length

  const didYouKnow = figure.did_you_know?.trim() ?? null
  const talkTopics = toStringArray(figure.talk_topics)
  const relatedFigures = toStringArray(figure.related_figures)
  const guestChatParams = new URLSearchParams()
  if (primaryPlaceSlug) {
    guestChatParams.set('place_slug', primaryPlaceSlug)
  }
  const buildGuestChatPath = (prompt?: string) => {
    const params = new URLSearchParams(guestChatParams)
    if (prompt?.trim()) {
      params.set('prompt', prompt.trim())
    }
    params.set('start_chat', '1')
    const suffix = params.toString()
    return `/guest/${figure.slug}${suffix ? `?${suffix}` : ''}`
  }

  const guestChatPath = buildGuestChatPath()
  const chatButtonClasses = ['button', 'primary']
  if (startChatIntent) {
    chatButtonClasses.push('button-chat-highlight')
  }
  const chatButtonLabel = startChatIntent ? `Resume chat with ${figure.name}` : `Talk to ${figure.name}`
  const talkTopicsCopy = talkTopics.length > 0 ? talkTopics : []
  const relatedFiguresCopy = relatedFigures.length > 0 ? relatedFigures : []

  const formatFigureName = (slugValue: string): string => {
    const normalized = normalizeSlug(slugValue)
    const knownName = figureDirectory[normalized]?.name
    if (knownName) return knownName
    return slugValue
      .split(/[-_]/)
      .filter(Boolean)
      .map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1))
      .join(' ')
  }

  const buildTopicQuestion = (topic: string): string => `Could we talk about ${topic}?`

  const shouldShowAtGlance = Boolean(rolesLine || associatedPlaces.length > 0 || pronunciation)

  return (
    <article className="detail-page person-detail">
      <section className="person-hero">
        {portraitSrc && (
          <div className="person-hero-media">
            <img
              src={portraitSrc}
              alt={`Portrait of ${figure.name}`}
              loading="lazy"
              decoding="async"
              width="720"
              height="480"
              onError={(event) => {
                event.currentTarget.src = fallbackFigureImage
              }}
              style={{ objectFit: 'cover', width: '100%', height: '100%' }}
            />
          </div>
        )}
        <div className="person-hero-body">
          {heroMetaLine && <p className="person-hero-meta">{heroMetaLine}</p>}
          <h1>{figure.name}</h1>
          {knownFor && <p className="person-hero-tagline">{knownFor}</p>}
          {summaryCopy && <p className="person-hero-summary">{summaryCopy}</p>}
          <div className="button-row" style={{ justifyContent: 'flex-start' }}>
            <Link className={chatButtonClasses.join(' ')} to={guestChatPath}>
              {chatButtonLabel}
            </Link>
          </div>
          <div className="source-depth-profile-panel">
            <SourceDepthBadge depth={figure.source_depth} variant="panel" />
            <p>Answers may draw on this supporting material when relevant.</p>
          </div>
          {startChatIntent && (
            <p className="chat-start-hint" role="status">
              We will take you straight into the Places in Time guest chat experience.
            </p>
          )}
        </div>
      </section>

      {error && !notFound && <p className="error-state">{error}</p>}

      <section className="person-grid">
        {shouldShowAtGlance && (
          <div className="person-card">
            <h2>At a glance</h2>
            <dl className="person-fact-grid">
              {rolesLine && (
                <div>
                  <dt>Roles</dt>
                  <dd>{rolesLine}</dd>
                </div>
              )}
              {associatedPlaces.length > 0 && (
                <div>
                  <dt>Most associated with</dt>
                  <dd>
                    {associatedPlaces.map((place, index) => (
                      <span key={place.slug ?? `${place.label}-${index}`}>
                        {place.slug ? <Link to={`/places/${place.slug}`}>{place.label}</Link> : place.label}
                        {index < associatedPlaces.length - 1 && ', '}
                      </span>
                    ))}
                  </dd>
                </div>
              )}
              {pronunciation && (
                <div>
                  <dt>Pronunciation</dt>
                  <dd>{pronunciation}</dd>
                </div>
              )}
            </dl>
          </div>
        )}

        {miniTimelineItems.length > 0 && (
          <div className="person-card person-card-timeline">
            <h2>Mini timeline</h2>
            <ul className="person-timeline">
              {miniTimelineItems.map((item, index) => (
                <li key={`${item.label ?? 'timeline'}-${index}`}>
                  {item.label && <span className="person-timeline-label">{item.label}</span>}
                  {item.text && <p>{item.text}</p>}
                </li>
              ))}
            </ul>
          </div>
        )}

        {didYouKnow && (
          <div className="person-card">
            <h2>Did you know?</h2>
            <p>{didYouKnow}</p>
          </div>
        )}

        {talkTopicsCopy.length > 0 && (
          <div className="person-card">
            <h2>Talk to me about…</h2>
            <p className="person-card-hint">Pick a prompt and we will queue it up for chat.</p>
            <div className="person-topic-actions">
              {talkTopicsCopy.map((topic, index) => {
                const question = buildTopicQuestion(topic)
                const topicPath = buildGuestChatPath(question)
                return (
                  <Link key={`${topic}-${index}`} className="pill-button" to={topicPath}>
                    {`Talk to me about ${topic}`}
                  </Link>
                )
              })}
            </div>
          </div>
        )}

        {relatedFiguresCopy.length > 0 && (
          <div className="person-card">
            <h2>Related figures</h2>
            <ul className="person-related-list">
              {relatedFiguresCopy.map((slugValue, index) => {
                const label = formatFigureName(slugValue)
                return (
                  <li key={`${slugValue}-${index}`}>
                    <Link to={`/people/${slugValue}`}>{label}</Link>
                  </li>
                )
              })}
            </ul>
          </div>
        )}

        {pitNarrative.length > 0 && (
          <div className="person-card">
            <h2>Life &amp; legacy</h2>
            {pitNarrativeTeaserParagraphs.map((paragraph, index) => {
              const isLastParagraph = index === pitNarrativeTeaserParagraphs.length - 1
              const shouldShowEllipsis = shouldTruncateNarrative && isLastParagraph
              const displayParagraph = shouldShowEllipsis && !paragraph.endsWith('…') ? `${paragraph}…` : paragraph
              return <p key={`pit-narrative-teaser-${index}`}>{displayParagraph}</p>
            })}
            <button
              type="button"
              className="button ghost"
              onClick={() => setShowFullNarrative((prev) => !prev)}
              aria-expanded={showFullNarrative}
            >
              {showFullNarrative ? 'Show less' : 'Read more'}
            </button>
          </div>
        )}
      </section>

      {pitNarrative.length > 0 && showFullNarrative && (
        <section className="person-narrative-panel" aria-label="Life and legacy full narrative">
          <div className="person-card">
            <h2>Life &amp; legacy</h2>
            {hasAccordionSections ? (
              <div className="person-accordion">
                {markdownSections?.intro && (
                  <div
                    className="person-narrative-html"
                    dangerouslySetInnerHTML={{ __html: renderMarkdownSafe(markdownSections.intro) }}
                  />
                )}
                {markdownSections?.sections.map((section, index) => (
                  <details key={`pit-narrative-accordion-${index}`}>
                    <summary>{section.title}</summary>
                    <div
                      className="person-narrative-html"
                      dangerouslySetInnerHTML={{ __html: renderMarkdownSafe(section.body) }}
                    />
                  </details>
                ))}
              </div>
            ) : pitNarrativeFormat === 'markdown' ? (
              <div className="person-narrative-html" dangerouslySetInnerHTML={{ __html: renderMarkdownSafe(pitNarrative) }} />
            ) : (
              pitNarrativeParagraphs.map((paragraph, index) => <p key={`pit-narrative-full-${index}`}>{paragraph}</p>)
            )}
            <div className="button-row">
              <button type="button" className="button ghost" onClick={() => setShowFullNarrative(false)} aria-expanded={showFullNarrative}>
                Show less
              </button>
            </div>
          </div>
        </section>
      )}

      <Link className="button" to="/people">
        Back to all people
      </Link>
    </article>
  )
}

export default Person
